import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Logo from '../components/Logo';
import { COUNTRIES, US_STATES } from '../constants/locations';
import axios from '../config/api';
import { API_URL } from '../config/api';
import { MdLock, MdArrowBack, MdKey } from 'react-icons/md';

function AuthPage() {
  const [searchParams] = useSearchParams();
  const [isLogin, setIsLogin] = useState(!searchParams.get('register'));
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    country: 'United States',
    state: '',
    zipCode: '',
    city: ''
  });
  const [lookingUpCity, setLookingUpCity] = useState(false);
  const [cityDetected, setCityDetected] = useState(false);
  const [error, setError] = useState('');
  const [cityError, setCityError] = useState('');
  const [usernameError, setUsernameError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);
  const [mfaLoading, setMfaLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading, login, loginWithMfa, loginWithRecoveryCode, cancelMfa, register, mfaRequired } = useAuth();
  
  const isUSSelected = formData.country === 'United States';

  // Redirect if already authenticated
  useEffect(() => {
    if (!loading && user) {
      navigate('/home');
    }
  }, [user, loading, navigate]);

  // Check for Google auth error from redirect
  useEffect(() => {
    if (location.state?.error) {
      setError(location.state.error);
    }
  }, [location.state]);

  // Check for register query param on mount
  useEffect(() => {
    if (searchParams.get('register') === 'true') {
      setIsLogin(false);
    }
  }, [searchParams]);

  // Validation constants
  const MAX_USERNAME_LENGTH = 30;
  const MAX_PASSWORD_LENGTH = 64;
  const MIN_USERNAME_LENGTH = 5;
  const MIN_PASSWORD_LENGTH = 6;

  // Common weak passwords to check against
  const COMMON_PASSWORDS = [
    'password', '123456', '12345678', 'qwerty', 'abc123', 'monkey', 'master',
    'dragon', 'letmein', 'login', 'welcome', 'admin', 'passw0rd', 'hello'
  ];

  // Calculate password strength
  const getPasswordStrength = (password) => {
    if (!password) return { level: 0, label: '', color: '', width: '0%' };
    
    const length = password.length;
    const hasLowercase = /[a-z]/.test(password);
    const hasUppercase = /[A-Z]/.test(password);
    const hasNumbers = /[0-9]/.test(password);
    const hasSymbols = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password);
    const isCommon = COMMON_PASSWORDS.some(common => 
      password.toLowerCase().includes(common)
    );
    
    const hasMixedCase = hasLowercase && hasUppercase;
    
    // Determine strength level based on new thresholds
    // Very Strong: 10+ chars, mixed case + numbers + symbols
    if (length >= 10 && hasMixedCase && hasNumbers && hasSymbols && !isCommon) {
      return { level: 4, label: 'Very Strong', color: 'bg-green-600', width: '100%' };
    }
    
    // Strong: 8+ chars, mixed case + numbers + symbols
    if (length >= 8 && hasMixedCase && hasNumbers && hasSymbols && !isCommon) {
      return { level: 3, label: 'Strong', color: 'bg-blue-500', width: '75%' };
    }
    
    // Moderate: 8-10 chars, mixed case + numbers
    if (length >= 8 && length <= 10 && hasMixedCase && hasNumbers && !isCommon) {
      return { level: 2, label: 'Moderate', color: 'bg-yellow-500', width: '50%' };
    }
    
    // Weak: <8 chars, only letters/numbers, or common words
    return { level: 1, label: 'Weak', color: 'bg-red-500', width: '25%' };
  };

  const passwordStrength = getPasswordStrength(formData.password);

  // Sanitize input to remove potentially dangerous characters
  const sanitizeInput = (value) => {
    if (!value) return value;
    // Remove SQL injection patterns and special characters
    return value
      .replace(/['";\\]/g, '') // Remove quotes, semicolons, backslashes
      .replace(/--/g, '')      // Remove SQL comments
      .replace(/<[^>]*>/g, '') // Remove HTML tags
      .trim();
  };

  // Validate username format
  const validateUsername = (username) => {
    if (!username) return '';
    if (username.length < MIN_USERNAME_LENGTH) {
      return `Username must be at least ${MIN_USERNAME_LENGTH} characters`;
    }
    if (username.length > MAX_USERNAME_LENGTH) {
      return `Username must be ${MAX_USERNAME_LENGTH} characters or less`;
    }
    // Only allow letters, numbers, underscores, and dots
    if (!/^[a-zA-Z0-9_.]+$/.test(username)) {
      return 'Username can only contain letters, numbers, underscores, and dots';
    }
    return '';
  };

  // Validate password
  const validatePassword = (password) => {
    if (!password) return '';
    if (password.length < MIN_PASSWORD_LENGTH) {
      return `Password must be at least ${MIN_PASSWORD_LENGTH} characters`;
    }
    if (password.length > MAX_PASSWORD_LENGTH) {
      return `Password must be ${MAX_PASSWORD_LENGTH} characters or less`;
    }
    return '';
  };

  // Handle username change with validation
  const handleUsernameChange = (e) => {
    const value = e.target.value;
    // Block input if exceeds max length
    if (value.length > MAX_USERNAME_LENGTH) {
      return;
    }
    const sanitized = sanitizeInput(value);
    setFormData({ ...formData, username: sanitized });
    if (!isLogin) {
      setUsernameError(validateUsername(sanitized));
    }
  };

  // Handle password change with validation
  const handlePasswordChange = (e) => {
    const value = e.target.value;
    // Block input if exceeds max length
    if (value.length > MAX_PASSWORD_LENGTH) {
      return;
    }
    setFormData({ ...formData, password: value });
    if (!isLogin) {
      setPasswordError(validatePassword(value));
    }
  };

  // Lookup city when user stops typing (debounced)
  useEffect(() => {
    if (!formData.city || formData.city.length < 2 || !isUSSelected) {
      setCityDetected(false);
      return;
    }
    const timer = setTimeout(async () => {
      setLookingUpCity(true);
      setCityError('');
      try {
        const response = await axios.post(`${API_URL}/api/location/lookup-city`, { city: formData.city });
        setFormData(prev => ({
          ...prev,
          state: response.data.state,
          zipCode: response.data.zipCode,
        }));
        setCityDetected(true);
        setCityError('');
      } catch (error) {
        setCityDetected(false);
        if (error.response?.status === 404) {
          setCityError(error.response?.data?.detail || 'City not recognized');
        }
      } finally {
        setLookingUpCity(false);
      }
    }, 600);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.city, isUSSelected]);

  const handleCityChange = (e) => {
    const value = e.target.value;
    setFormData({ ...formData, city: value });
    setCityDetected(false);
    setCityError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setUsernameError('');
    setPasswordError('');
    
    // Validate username and password for registration
    if (!isLogin) {
      const usernameErr = validateUsername(formData.username);
      const passwordErr = validatePassword(formData.password);
      
      if (usernameErr) {
        setUsernameError(usernameErr);
        return;
      }
      if (passwordErr) {
        setPasswordError(passwordErr);
        return;
      }
    }
    
    if (!isLogin && isUSSelected) {
      if (!formData.city || formData.city.trim().length < 2) {
        setError('Please enter your city');
        return;
      }
      if (!formData.state) {
        setError('Please select your state or enter a valid city to auto-detect it');
        return;
      }
    }
    
    try {
      if (isLogin) {
        const result = await login(formData.username, formData.password);
        // If MFA is required, the component will re-render to show MFA form
        if (!result?.mfaRequired) {
          navigate('/home');
        }
      } else {
        await register(formData);
        navigate('/home');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'An error occurred');
    }
  };

  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMfaLoading(true);
    
    try {
      if (useRecoveryCode) {
        const result = await loginWithRecoveryCode(recoveryCode);
        if (result.remainingCodes <= 2) {
          // Show warning about low recovery codes
          alert(`Login successful! Warning: You only have ${result.remainingCodes} recovery codes remaining. Consider regenerating them in Account Settings.`);
        }
      } else {
        await loginWithMfa(otpCode);
      }
      navigate('/home');
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid code. Please try again.');
    } finally {
      setMfaLoading(false);
    }
  };

  const handleCancelMfa = () => {
    cancelMfa();
    setOtpCode('');
    setRecoveryCode('');
    setUseRecoveryCode(false);
    setError('');
  };

  // MFA Verification Form
  if (mfaRequired) {
    return (
      <div className="min-h-screen bg-gradient-civote flex items-center justify-center p-4">
        <div className="bg-white rounded-card shadow-card w-full max-w-md p-8" data-testid="mfa-page">
          <div className="flex flex-col items-center mb-6">
            <div className="p-4 bg-primary/10 rounded-full mb-4">
              <MdLock className="w-12 h-12 text-primary" />
            </div>
            <h1 className="text-2xl font-bold text-gray-800 font-rubik">Two-Factor Authentication</h1>
            <p className="text-gray-600 mt-2 font-rubik text-center">
              {useRecoveryCode 
                ? 'Enter a recovery code to sign in'
                : 'Enter the 6-digit code from your authenticator app'}
            </p>
          </div>

          <form onSubmit={handleMfaSubmit} className="space-y-4" data-testid="mfa-form">
            {!useRecoveryCode ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 font-rubik">
                  Authentication Code
                </label>
                <input
                  type="text"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-card focus:ring-2 focus:ring-primary focus:border-primary font-rubik text-center text-2xl tracking-widest"
                  placeholder="000000"
                  maxLength={6}
                  autoFocus
                  required
                  data-testid="otp-input"
                />
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 font-rubik">
                  Recovery Code
                </label>
                <input
                  type="text"
                  value={recoveryCode}
                  onChange={(e) => setRecoveryCode(e.target.value.toUpperCase())}
                  className="w-full px-4 py-3 border border-gray-300 rounded-card focus:ring-2 focus:ring-primary focus:border-primary font-rubik text-center text-lg tracking-widest"
                  placeholder="XXXX-XXXX"
                  maxLength={9}
                  autoFocus
                  required
                  data-testid="recovery-code-input"
                />
              </div>
            )}

            {error && (
              <div className="bg-red-50 text-danger p-3 rounded-card text-sm font-rubik" data-testid="mfa-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={mfaLoading || (useRecoveryCode ? recoveryCode.length < 8 : otpCode.length !== 6)}
              className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="mfa-submit-button"
            >
              {mfaLoading ? 'Verifying...' : 'Verify'}
            </button>

            <div className="flex items-center justify-between pt-2">
              <button
                type="button"
                onClick={handleCancelMfa}
                className="text-gray-500 hover:text-gray-700 text-sm font-rubik flex items-center gap-1"
                data-testid="mfa-cancel-button"
              >
                <MdArrowBack className="w-4 h-4" />
                Back to login
              </button>
              
              <button
                type="button"
                onClick={() => {
                  setUseRecoveryCode(!useRecoveryCode);
                  setOtpCode('');
                  setRecoveryCode('');
                  setError('');
                }}
                className="text-primary hover:text-primary-dark text-sm font-rubik flex items-center gap-1"
                data-testid="toggle-recovery-button"
              >
                <MdKey className="w-4 h-4" />
                {useRecoveryCode ? 'Use authenticator code' : 'Use recovery code'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-civote flex items-center justify-center p-4">
      <div className="bg-white rounded-card shadow-card w-full max-w-md p-8" data-testid="auth-page">
        <div className="flex flex-col items-center mb-8">
          <Logo size="xlarge" />
          <p className="text-gray-600 mt-4 font-rubik">Your voice matters</p>
        </div>

        <div className="flex mb-6 bg-civote-bg rounded-card p-1">
          <button
            onClick={() => setIsLogin(true)}
            className={`flex-1 py-2 rounded-card transition-all font-rubik font-medium ${isLogin ? 'bg-primary text-white' : 'text-gray-600'}`}
            data-testid="login-tab-button"
          >
            Login
          </button>
          <button
            onClick={() => setIsLogin(false)}
            className={`flex-1 py-2 rounded-card transition-all font-rubik font-medium ${!isLogin ? 'bg-primary text-white' : 'text-gray-600'}`}
            data-testid="register-tab-button"
          >
            Register
          </button>
        </div>

        {/* Google Sign In Button */}
        <button
          type="button"
          onClick={() => {
            setGoogleLoading(true);
            // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
            const redirectUri = window.location.origin + '/auth/google';
            const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
            const googleAuthUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${encodeURIComponent(clientId)}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code&scope=openid%20email%20profile&access_type=offline&prompt=consent`;
            window.location.href = googleAuthUrl;
          }}
          disabled={googleLoading}
          className="w-full flex items-center justify-center gap-3 px-4 py-2.5 border border-gray-300 rounded-card bg-white hover:bg-gray-50 transition-colors font-rubik text-sm font-medium text-gray-700 mb-4 disabled:opacity-50"
          data-testid="google-sign-in-button"
        >
          {googleLoading ? (
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-500"></div>
          ) : (
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
          )}
          {googleLoading ? 'Redirecting...' : `Sign ${isLogin ? 'in' : 'up'} with Google`}
        </button>

        <div className="flex items-center gap-3 mb-4">
          <div className="flex-1 h-px bg-gray-200"></div>
          <span className="text-xs text-gray-400 font-rubik">or</span>
          <div className="flex-1 h-px bg-gray-200"></div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4" data-testid="auth-form">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 font-rubik">
              Username
              {!isLogin && <span className="text-gray-400 font-normal ml-1">(max {MAX_USERNAME_LENGTH} chars)</span>}
            </label>
            <input
              type="text"
              value={formData.username}
              onChange={handleUsernameChange}
              maxLength={MAX_USERNAME_LENGTH}
              className={`w-full px-4 py-2 border rounded-card focus:ring-2 focus:ring-primary focus:border-primary font-rubik ${
                usernameError ? 'border-red-500' : 'border-gray-300'
              }`}
              required
              data-testid="username-input"
            />
            {usernameError && (
              <p className="text-red-600 text-xs mt-1" data-testid="username-error">{usernameError}</p>
            )}
            {!isLogin && !usernameError && formData.username && (
              <p className="text-gray-400 text-xs mt-1">{formData.username.length}/{MAX_USERNAME_LENGTH} characters</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 font-rubik">
              Password
              {!isLogin && <span className="text-gray-400 font-normal ml-1">(6-{MAX_PASSWORD_LENGTH} chars)</span>}
            </label>
            <input
              type="password"
              value={formData.password}
              onChange={handlePasswordChange}
              maxLength={MAX_PASSWORD_LENGTH}
              className={`w-full px-4 py-2 border rounded-card focus:ring-2 focus:ring-primary focus:border-primary font-rubik ${
                passwordError ? 'border-red-500' : 'border-gray-300'
              }`}
              required
              data-testid="password-input"
            />
            {passwordError && (
              <p className="text-red-600 text-xs mt-1" data-testid="password-error">{passwordError}</p>
            )}
            {/* Password Strength Meter - Only shown during registration */}
            {!isLogin && formData.password && (
              <div className="mt-2" data-testid="password-strength-meter">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-gray-500">Password Strength:</span>
                  <span className={`text-xs font-medium ${
                    passwordStrength.level === 4 ? 'text-green-600' :
                    passwordStrength.level === 3 ? 'text-blue-500' :
                    passwordStrength.level === 2 ? 'text-yellow-600' :
                    'text-red-500'
                  }`} data-testid="password-strength-label">
                    {passwordStrength.label}
                  </span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className={`h-full ${passwordStrength.color} transition-all duration-300`}
                    style={{ width: passwordStrength.width }}
                    data-testid="password-strength-bar"
                  />
                </div>
                <div className="mt-1 text-xs text-gray-400">
                  {passwordStrength.level < 3 && (
                    <span>
                      Tips: {passwordStrength.level < 2 ? 'Add uppercase, numbers' : 'Add symbols (!@#$%)'} for stronger password
                    </span>
                  )}
                  {passwordStrength.level >= 3 && passwordStrength.level < 4 && (
                    <span>Use 16+ characters for maximum strength</span>
                  )}
                  {passwordStrength.level === 4 && (
                    <span className="text-green-600">✓ Excellent password!</span>
                  )}
                </div>
              </div>
            )}
            {!isLogin && !passwordError && formData.password && (
              <p className="text-gray-400 text-xs mt-1">{formData.password.length}/{MAX_PASSWORD_LENGTH} characters</p>
            )}
          </div>

          {!isLogin && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                <select
                  value={formData.country}
                  onChange={(e) => {
                    const newCountry = e.target.value;
                    setFormData({ 
                      ...formData, 
                      country: newCountry,
                      state: newCountry === 'United States' ? '' : 'N/A',
                      zipCode: newCountry === 'United States' ? '' : 'N/A',
                      city: newCountry === 'United States' ? '' : ''
                    });
                    setCityError('');
                    setCityDetected(false);
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-card focus:ring-2 focus:ring-primary focus:border-primary bg-white font-rubik"
                  required
                  data-testid="country-input"
                >
                  {COUNTRIES.map(country => (
                    <option key={country} value={country}>{country}</option>
                  ))}
                </select>
              </div>

              {isUSSelected && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                    <div className="relative">
                      <input
                        type="text"
                        value={formData.city}
                        onChange={handleCityChange}
                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent font-rubik ${
                          cityError ? 'border-red-500' : cityDetected ? 'border-green-500' : 'border-gray-300'
                        }`}
                        required
                        placeholder="e.g. Bothell, Savoy, Springfield..."
                        data-testid="city-input"
                      />
                      {lookingUpCity && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                        </div>
                      )}
                    </div>
                    {cityError && (
                      <p className="text-red-600 text-xs mt-1" data-testid="city-error">{cityError}</p>
                    )}
                    {cityDetected && !cityError && formData.state && formData.zipCode && (
                      <p className="text-green-600 text-xs mt-1 flex items-center gap-1" data-testid="city-detected">
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        Detected: <strong>{formData.city}, {formData.state} {formData.zipCode}</strong>
                      </p>
                    )}
                    {!cityDetected && !cityError && formData.city.length < 2 && (
                      <p className="text-gray-500 text-xs mt-1">Type your city name to auto-detect state and zip code</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                    <select
                      value={formData.state}
                      onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-card focus:ring-2 focus:ring-primary focus:border-primary bg-white font-rubik"
                      required
                      data-testid="state-input"
                    >
                      <option value="">Select a state...</option>
                      {US_STATES.map(state => (
                        <option key={state} value={state}>{state}</option>
                      ))}
                    </select>
                    {cityDetected && formData.state && (
                      <p className="text-gray-500 text-xs mt-1">Auto-filled from city. Change if incorrect.</p>
                    )}
                  </div>
                </>
              )}

              <div className="bg-blue-50 border border-secondary rounded-card p-3 font-rubik" data-testid="privacy-disclaimer">
                <div className="flex items-start space-x-2">
                  <svg className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <p className="text-xs text-blue-800">
                    <strong>Privacy Notice:</strong> We respect your privacy. Your location is used to provide relevant local poll questions. This information is stored securely.
                  </p>
                </div>
              </div>
            </>
          )}

          {error && (
            <div className="bg-red-50 text-danger p-3 rounded-card text-sm font-rubik" data-testid="error-message">
              {error}
            </div>
          )}

          <button
            type="submit"
            className="w-full btn-primary"
            data-testid="submit-button"
          >
            {isLogin ? 'Login' : 'Register'}
          </button>
          
          {isLogin && (
            <div className="mt-3 text-center">
              <Link 
                to="/forgot-password" 
                className="text-gray-500 hover:text-primary text-sm font-rubik"
                data-testid="forgot-password-link"
              >
                Forgot your password?
              </Link>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}

export default AuthPage;
